/**
 * @file Event create/edit modal owner for the Shared Calendar domain.
 */

import { appData, state } from "../../app/state/state.js";
import { db } from "../../app/firebase/firebase.js";
import {
    doc,
    getDoc,
} from "https://www.gstatic.com/firebasejs/12.19.0/firebase-firestore.js";
import { closeForm } from "../../shared/forms/modalShell.js";
import { openStandardForm } from "../../shared/forms/standardFormModal.js";
import {
    bindNativeFieldBehavior,
    FIXED_TEXTAREA_CLASSES,
    getNativeFieldWrapperHtml,
    NATIVE_CHECKBOX_CLASSES,
    NATIVE_CHECKBOX_ICON_CLASSES,
    NATIVE_CHECKBOX_LABEL_CLASSES,
    NATIVE_CHECKBOX_MARKER_CLASSES,
    NATIVE_INPUT_CLASSES,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    getRegisteredControlValues,
} from "../../shared/ui/controls/controlCore.js";
import {
    createUserOptions,
} from "../../shared/ui/controls/controlOptions.js";
import { createDateControl } from "../../shared/ui/controls/dateControl.js";
import { createUserSelectPreset } from "../../shared/ui/controls/controlPresets.js";
import { createSelectControl } from "../../shared/ui/controls/selectControl.js";
import { escapeHTML } from "../../shared/utils/contentUtils.js";
import {
    createCalendarEvent,
    updateCalendarEvent,
} from "./calendarApi.js";
import {
    showCalendarErrorModal,
} from "./calendarModalFeedback.js";
import {
    addOptimisticEventEntry,
    discardOptimisticEntry,
    reconcileOptimisticEventEntry,
} from "./calendarOptimisticEntries.js";
import {
    bindCalendarItemThresholdControl,
    CALENDAR_THRESHOLD_ITEM_TYPES,
    getCalendarItemThresholdDraft,
    readCalendarItemThresholds,
    renderCalendarItemThresholdControl,
    syncCalendarItemThresholdControl,
} from "./notificationThresholdControls.js";

const DATE_RANGE_ERROR_MESSAGE = "End date cannot be before start date.";
const TIME_RANGE_ERROR_MESSAGE = "End time must be after start time.";
const ALL_USERS_VALUE = "__all_users__";
const TIME_INPUT_CLASSES = [
    "calendar-time-input",
    "dfc-date-control-input",
    NATIVE_INPUT_CLASSES,
    "pr-9",
].join(" ");
const TIME_PICKER_BUTTON_CLASSES = [
    "absolute inset-y-0 right-1 my-auto hidden h-7 w-7 sm:inline-flex",
    "items-center justify-center rounded text-gray-500",
    "transition-colors hover:text-gray-700 focus:outline-none",
    "disabled:cursor-not-allowed disabled:text-gray-400",
].join(" ");
function attrValue(value) {
    return escapeHTML(String(value ?? ""))
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}

function getCurrentUserId() {
    return state.currentUser?.uid || state.currentUserProfile?.id || "";
}

function userHasCalendarAccess(user) {
    return user?.role === "admin" ||
        (Array.isArray(user?.permissions) && user.permissions.includes("calendar"));
}

function getSelectableCalendarUsers() {
    return (appData.users || [])
        .filter((user) => user?.id && user.isActive !== false)
        .filter(userHasCalendarAccess);
}

function getTodayDateKey() {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
}

function parseTimeMinutes(time) {
    const match = /^(\d{2}):(\d{2})$/.exec(String(time || ""));
    if (!match) {return null;}
    const hours = Number(match[1]);
    const minutes = Number(match[2]);
    if (hours > 23 || minutes > 59) {return null;}
    return (hours * 60) + minutes;
}

function getInitialEventData(event) {
    const today = getTodayDateKey();
    const data = {
        id: event?.id || event?.sourceId || "",
        title: event?.title || "",
        startDate: event?.startDate || today,
        endDate: event?.endDate || event?.startDate || today,
        startTime: event?.startTime || "",
        endTime: event?.endTime || "",
        isFullDay: event?.isFullDay === true,
        description: event?.description || "",
        hostIds: Array.isArray(event?.hostIds) && event.hostIds.length ?
            event.hostIds :
            [getCurrentUserId()].filter(Boolean),
        attendeeIds: Array.isArray(event?.attendeeIds) ? event.attendeeIds : [],
        allCalendarUsers: event?.allCalendarUsers === true,
        eventDate: event?.startDate || today,
        notifyUsers: !(event?.id || event?.sourceId),
        updatedAt: event?.updatedAt || null,
    };
    data.notificationThreshold = getCalendarItemThresholdDraft({
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
        item: event,
        config: appData.config || {},
        isAllDay: data.isFullDay,
    });
    return data;
}

async function resolveEventFormSource(event) {
    const sourceId = event?.sourceType === "event" ?
        event?.sourceId || event?.id || "" :
        "";
    if (!sourceId || Array.isArray(event?.notificationThresholds)) {
        return event;
    }

    try {
        const snapshot = await getDoc(doc(db, "events", sourceId));
        if (!snapshot.exists()) {return event;}
        return { id: snapshot.id, ...snapshot.data() };
    } catch (error) {
        console.error("Error loading Calendar event for edit:", error);
        return event;
    }
}

function getTextInputHtml({
    name,
    label,
    value = "",
    type = "text",
    required = false,
}) {
    return `
        <input
            type="${attrValue(type)}"
            name="${attrValue(name)}"
            aria-label="${attrValue(label || name)}"
            value="${attrValue(value)}"
            class="${NATIVE_INPUT_CLASSES}"
            ${required ? "required" : ""}
            data-calendar-event-input
        >
    `;
}

function getTimeInputHtml({
    name,
    label,
    value = "",
    required = false,
}) {
    return `
        <div class="relative" data-calendar-time-control>
            <input
                type="time"
                name="${attrValue(name)}"
                aria-label="${attrValue(label || name)}"
                value="${attrValue(value)}"
                class="${TIME_INPUT_CLASSES}"
                ${required ? "required" : ""}
                data-calendar-empty="${value ? "false" : "true"}"
                data-calendar-event-input
            >
            <button
                type="button"
                class="${TIME_PICKER_BUTTON_CLASSES}"
                aria-label="Open time picker"
                tabindex="-1"
                data-calendar-time-picker-button
            >
                <span class="material-symbols-outlined text-[20px] leading-none" aria-hidden="true">expand_more</span>
            </button>
        </div>
    `;
}

function getCheckboxHtml({ name, checked = false, label }) {
    return `
        <label class="${NATIVE_CHECKBOX_LABEL_CLASSES}" data-native-checkbox-label>
            <input
                type="checkbox"
                name="${attrValue(name)}"
                class="${NATIVE_CHECKBOX_CLASSES}"
                ${checked ? "checked" : ""}
                data-calendar-event-input
            >
            <span class="${NATIVE_CHECKBOX_MARKER_CLASSES}" data-native-checkbox-marker>
                <span class="${NATIVE_CHECKBOX_ICON_CLASSES} opacity-0" data-native-checkbox-icon>check</span>
            </span>
            <span>${escapeHTML(label)}</span>
        </label>
    `;
}

function getAllDayToggleHtml(checked) {
    return `
        <label class="flex items-center gap-2 text-sm font-medium text-gray-700">
            <span>All day</span>
            <span class="toggle-switch shrink-0">
                <input
                    type="checkbox"
                    id="calendar-event-all-day-toggle"
                    name="isFullDay"
                    form="item-form"
                    ${checked ? "checked" : ""}
                    data-calendar-event-input
                >
                <span class="slider"></span>
            </span>
        </label>
    `;
}

function getUserControlHtml(name) {
    return `
        <div data-calendar-user-control="${attrValue(name)}"></div>
    `;
}

function getEventFieldsHtml(rawEvent) {
    const event = getInitialEventData(rawEvent);
    return `
        <div class="grid grid-cols-1 gap-4 md:grid-cols-2" data-calendar-event-fields>
            ${getNativeFieldWrapperHtml("Title", getTextInputHtml({
                name: "title",
                label: "Title",
                value: event.title,
                required: true,
            }), { className: "md:col-span-2", required: true })}
            <div class="grid gap-4 md:col-span-2 md:grid-cols-2" data-calendar-timed-field>
                <div data-calendar-date-control="eventDate"></div>
                <div class="grid min-w-0 gap-4 sm:grid-cols-2">
                    ${getNativeFieldWrapperHtml("Start time", getTimeInputHtml({
                        name: "startTime",
                        label: "Start time",
                        value: event.startTime,
                        required: true,
                    }), { required: true })}
                    ${getNativeFieldWrapperHtml("End time", getTimeInputHtml({
                        name: "endTime",
                        label: "End time",
                        value: event.endTime,
                        required: true,
                    }), { required: true })}
                </div>
            </div>
            <div data-calendar-all-day-date-field>
                <div data-calendar-date-control="startDate"></div>
            </div>
            <div class="calendar-all-day-end-field" data-calendar-all-day-date-field>
                <div data-calendar-date-control="endDate"></div>
            </div>
            ${getNativeFieldWrapperHtml("Hosts", getUserControlHtml(
                "hostIds",
            ), { required: true })}
            ${getNativeFieldWrapperHtml("Attendees", getUserControlHtml(
                "attendeeIds",
            ))}
            ${getNativeFieldWrapperHtml("Description", `
                <textarea
                    name="description"
                    rows="4"
                    class="${FIXED_TEXTAREA_CLASSES}"
                    data-calendar-event-input
                >${escapeHTML(event.description || "")}</textarea>
            `, { className: "md:col-span-2" })}
            <div class="md:col-span-2">
                ${renderCalendarItemThresholdControl({
                    itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
                    threshold: event.notificationThreshold,
                    label: "Event Reminders",
                    afterHeaderHtml: `
                        <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-gray-700" data-calendar-event-options-row>
                            ${getCheckboxHtml({
                                name: "notifyUsers",
                                checked: event.notifyUsers,
                                label: "Notify users now",
                            })}
                        </div>
                    `,
                    isAllDay: event.isFullDay,
                })}
            </div>
        </div>
    `;
}

function getAttendeeOptions(users, hostIds = []) {
    const hostSet = new Set((hostIds || []).map((id) => String(id)));
    const attendeeUsers = users
        .filter((user) => user?.id && !hostSet.has(String(user.id)));
    return [
        {
            value: ALL_USERS_VALUE,
            label: "All users",
        },
        ...createUserOptions(attendeeUsers),
    ];
}

function getEventControlValues(itemForm, { validate = false } = {}) {
    return getRegisteredControlValues(itemForm, { validate });
}

function collectEventData(itemForm, controlValues = {}) {
    const formData = new FormData(itemForm);
    const isFullDay = formData.get("isFullDay") === "on";
    const startDate = isFullDay ?
        String(controlValues.startDate || "") :
        String(controlValues.eventDate || "");
    const startTime = String(formData.get("startTime") || "");
    const endTime = String(formData.get("endTime") || "");
    const attendeeValues = Array.isArray(controlValues.attendeeIds) ?
        controlValues.attendeeIds :
        [];
    const allCalendarUsers = attendeeValues.includes(ALL_USERS_VALUE);
    const notificationThresholds = readCalendarItemThresholds(itemForm, {
        itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
        isAllDay: isFullDay,
    });
    return {
        title: String(formData.get("title") || "").trim(),
        startDate,
        endDate: isFullDay ? String(controlValues.endDate || startDate) : startDate,
        startTime: isFullDay ? null : startTime || null,
        endTime: isFullDay ? null : endTime || null,
        isFullDay,
        description: String(formData.get("description") || "").trim() || null,
        hostIds: Array.isArray(controlValues.hostIds) ? controlValues.hostIds : [],
        attendeeIds: attendeeValues.filter((value) => value !== ALL_USERS_VALUE),
        allCalendarUsers,
        notificationThresholds,
    };
}

function renderDateRangeError(itemForm, message) {
    const errorContainer = itemForm.querySelector("#form-fields") || itemForm;
    errorContainer
        .querySelectorAll("[data-calendar-event-date-error]")
        .forEach((element) => element.remove());
    errorContainer.insertAdjacentHTML("afterbegin", `
        <p class="mb-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700" data-calendar-event-date-error>
            ${escapeHTML(message)}
        </p>
    `);
}

function clearDateRangeError(itemForm) {
    itemForm
        .querySelectorAll("[data-calendar-event-date-error]")
        .forEach((element) => element.remove());
}

function validateEventDateRange(itemForm, { showError = false } = {}) {
    const checkbox = document.getElementById("calendar-event-all-day-toggle");
    const startInput = itemForm
        .querySelector("input[type=\"date\"][name=\"startDate\"]");
    const endInput = itemForm
        .querySelector("input[type=\"date\"][name=\"endDate\"]");
    const isFullDay = checkbox?.checked === true;

    if (!endInput) {return true;}

    if (!isFullDay) {
        endInput.setCustomValidity("");
        clearDateRangeError(itemForm);
        return true;
    }

    const isInvalid = Boolean(
        startInput?.value &&
        endInput.value &&
        endInput.value < startInput.value,
    );
    endInput.setCustomValidity(isInvalid ? DATE_RANGE_ERROR_MESSAGE : "");

    if (isInvalid && showError) {
        renderDateRangeError(itemForm, DATE_RANGE_ERROR_MESSAGE);
    } else if (!isInvalid) {
        clearDateRangeError(itemForm);
    }

    return !isInvalid;
}

function validateEventTimeRange(itemForm) {
    const checkbox = document.getElementById("calendar-event-all-day-toggle");
    const startInput = itemForm
        .querySelector("input[type=\"time\"][name=\"startTime\"]");
    const endInput = itemForm
        .querySelector("input[type=\"time\"][name=\"endTime\"]");
    const isFullDay = checkbox?.checked === true;

    if (!endInput) {return true;}
    if (isFullDay) {
        endInput.setCustomValidity("");
        return true;
    }

    const startMinutes = parseTimeMinutes(startInput?.value);
    const endMinutes = parseTimeMinutes(endInput.value);
    const isInvalid = startMinutes !== null &&
        endMinutes !== null &&
        endMinutes <= startMinutes;
    endInput.setCustomValidity(isInvalid ? TIME_RANGE_ERROR_MESSAGE : "");
    return !isInvalid;
}

function bindAllDayFields(itemForm, controls = {}) {
    const checkbox = document.getElementById("calendar-event-all-day-toggle");
    const startInput = () => itemForm
        .querySelector("input[type=\"date\"][name=\"startDate\"]");
    const endInput = () => itemForm
        .querySelector("input[type=\"date\"][name=\"endDate\"]");
    const timedDateInput = () => itemForm
        .querySelector("input[type=\"date\"][name=\"eventDate\"]");
    const timedTimeInputs = () => Array.from(itemForm
        .querySelectorAll("input[type=\"time\"]"));
    const endDateField = itemForm.querySelector(".calendar-all-day-end-field");
    const timedDateField = itemForm.querySelector("[data-calendar-timed-field]");
    const allDayDateFields = Array.from(itemForm.querySelectorAll("[data-calendar-all-day-date-field]"));
    const syncLayout = () => {
        const isFullDay = checkbox?.checked === true;
        timedDateField?.classList.toggle("hidden", isFullDay);
        allDayDateFields.forEach((field) => {
            field.classList.toggle("hidden", !isFullDay);
        });
        endDateField?.classList.toggle("hidden", !isFullDay);
        controls.eventDateControl?.update({
            disabled: isFullDay,
            required: !isFullDay,
        });
        controls.startDateControl?.update({
            disabled: !isFullDay,
            required: isFullDay,
        });
        controls.endDateControl?.update({
            disabled: !isFullDay,
            required: isFullDay,
        });
        timedTimeInputs().forEach((timeInput) => {
            timeInput.disabled = isFullDay;
            timeInput.required = !isFullDay;
            timeInput.setCustomValidity("");
            const pickerButton = timeInput
                .closest("[data-calendar-time-control]")
                ?.querySelector("[data-calendar-time-picker-button]");
            if (pickerButton) {
                pickerButton.disabled = isFullDay;
            }
        });
        timedDateInput()?.setCustomValidity("");
        syncCalendarItemThresholdControl(itemForm, {
            itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
            isAllDay: isFullDay,
        });
        validateEventDateRange(itemForm, { showError: true });
        validateEventTimeRange(itemForm);
    };
    const syncEndToStart = () => {
        const start = startInput();
        const end = endInput();
        if (start && end) {
            end.value = start.value || end.value;
            end.dispatchEvent(new Event("change", { bubbles: true }));
        }
    };
    const bindDateInputListeners = () => {
        const start = startInput();
        const end = endInput();
        if (start && start.dataset.calendarDateValidationBound !== "true") {
            start.dataset.calendarDateValidationBound = "true";
            start.addEventListener("input", () => {
                syncEndToStart();
                syncLayout();
                requestAnimationFrame(bindDateInputListeners);
            });
            start.addEventListener("change", () => {
                syncEndToStart();
                syncLayout();
                requestAnimationFrame(bindDateInputListeners);
            });
        }
        if (end && end.dataset.calendarDateValidationBound !== "true") {
            end.dataset.calendarDateValidationBound = "true";
            end.addEventListener("input", () =>
                validateEventDateRange(itemForm, { showError: true }));
            end.addEventListener("change", () =>
                validateEventDateRange(itemForm, { showError: true }));
        }
    };
    itemForm.__calendarDateValidationObserver?.disconnect?.();
    itemForm.__calendarDateValidationObserver = new MutationObserver(() => {
        bindDateInputListeners();
    });
    itemForm.__calendarDateValidationObserver.observe(itemForm, {
        childList: true,
        subtree: true,
    });
    if (itemForm.__calendarDateValidationCaptureHandler) {
        document.removeEventListener(
            "input",
            itemForm.__calendarDateValidationCaptureHandler,
            true,
        );
        document.removeEventListener(
            "change",
            itemForm.__calendarDateValidationCaptureHandler,
            true,
        );
    }
    itemForm.__calendarDateValidationCaptureHandler = (event) => {
        if (!itemForm.contains(event.target)) {return;}
        if (event.target?.matches?.("input[name=\"startDate\"]")) {
            syncEndToStart();
            syncLayout();
            requestAnimationFrame(bindDateInputListeners);
        } else if (event.target?.matches?.("input[name=\"endDate\"]")) {
            validateEventDateRange(itemForm, { showError: true });
        } else if (event.target?.matches?.("input[type=\"time\"]")) {
            validateEventTimeRange(itemForm);
        }
    };
    document.addEventListener(
        "input",
        itemForm.__calendarDateValidationCaptureHandler,
        true,
    );
    document.addEventListener(
        "change",
        itemForm.__calendarDateValidationCaptureHandler,
        true,
    );
    clearInterval(itemForm.__calendarDateValidationInterval);
    itemForm.__calendarDateValidationInterval = setInterval(() => {
        const modalOpen = document.getElementById("form-modal")
            ?.classList.contains("flex");
        if (modalOpen) {
            validateEventDateRange(itemForm, { showError: true });
        }
    }, 150);
    checkbox?.addEventListener("change", () => {
        syncEndToStart();
        syncLayout();
        requestAnimationFrame(bindDateInputListeners);
    });
    itemForm.addEventListener("input", (event) => {
        if (event.target?.matches?.("input[name=\"startDate\"]")) {
            syncEndToStart();
            syncLayout();
            requestAnimationFrame(bindDateInputListeners);
        } else if (event.target?.matches?.("input[name=\"endDate\"]")) {
            validateEventDateRange(itemForm, { showError: true });
        } else if (event.target?.matches?.("input[type=\"time\"]")) {
            validateEventTimeRange(itemForm);
        }
    });
    itemForm.addEventListener("change", (event) => {
        if (event.target?.matches?.("input[name=\"startDate\"]")) {
            syncEndToStart();
            syncLayout();
            requestAnimationFrame(bindDateInputListeners);
        } else if (event.target?.matches?.("input[name=\"endDate\"]")) {
            validateEventDateRange(itemForm, { showError: true });
        } else if (event.target?.matches?.("input[type=\"time\"]")) {
            validateEventTimeRange(itemForm);
        }
    });
    if (checkbox?.checked !== true) {
        syncEndToStart();
    }
    syncLayout();
    bindDateInputListeners();
}

function bindTimeFieldBehavior(itemForm) {
    const inputs = Array.from(itemForm.querySelectorAll("input[type=\"time\"]"));
    if (!inputs.length) {return;}

    const syncEmptyState = (input) => {
        input.dataset.calendarEmpty = input.value ? "false" : "true";
    };
    const openPicker = (input) => {
        if (input.disabled) {return;}
        input.focus({ preventScroll: true });
        if (typeof input.showPicker === "function") {
            try {
                input.showPicker();
            } catch {
                // Focus-only fallback for browsers that reject showPicker.
            }
        }
    };

    inputs.forEach((input) => {
        input.addEventListener("input", () => {
            syncEmptyState(input);
            validateEventTimeRange(itemForm);
        });
        input.addEventListener("change", () => {
            syncEmptyState(input);
            validateEventTimeRange(itemForm);
        });
        input
            .closest("[data-calendar-time-control]")
            ?.querySelector("[data-calendar-time-picker-button]")
            ?.addEventListener("click", () => openPicker(input));
        syncEmptyState(input);
    });
}

function mountUserControls(itemForm, eventData) {
    const users = getSelectableCalendarUsers();
    const hostRoot = itemForm.querySelector("[data-calendar-user-control=\"hostIds\"]");
    const attendeeRoot = itemForm.querySelector("[data-calendar-user-control=\"attendeeIds\"]");
    const attendeeValue = eventData.allCalendarUsers ?
        [ALL_USERS_VALUE] :
        eventData.attendeeIds;
    let attendeeControl = null;
    let syncingAttendees = false;

    const syncAttendeeOptions = (hostIds = []) => {
        if (!attendeeControl) {return;}
        const nextOptions = getAttendeeOptions(users, hostIds);
        const allowedValues = new Set(nextOptions.map((option) => option.value));
        const currentValue = Array.isArray(attendeeControl.value) ?
            attendeeControl.value :
            [];
        const nextValue = currentValue.filter((value) => allowedValues.has(value));
        attendeeControl.update({
            options: nextOptions,
            value: nextValue,
        });
    };

    if (hostRoot) {
        createUserSelectPreset({
            root: hostRoot,
            appData,
            users,
            collectionName: "calendar",
            name: "hostIds",
            value: eventData.hostIds,
            multiple: true,
            searchable: true,
            required: true,
            portalMenu: true,
            requiredMessage: "Host is required.",
            placeholder: "+ Add hosts",
            onChange: (value) => syncAttendeeOptions(value),
        });
    }

    if (attendeeRoot) {
        attendeeControl = createSelectControl({
            root: attendeeRoot,
            name: "attendeeIds",
            value: attendeeValue,
            options: getAttendeeOptions(users, eventData.hostIds),
            multiple: true,
            searchable: true,
            portalMenu: true,
            placeholder: "+ Add attendees",
            accessibleName: "Attendees",
            onChange: (value) => {
                if (syncingAttendees || !Array.isArray(value)) {return;}
                if (value.includes(ALL_USERS_VALUE) && value.length > 1) {
                    syncingAttendees = true;
                    attendeeControl.setValue([ALL_USERS_VALUE]);
                    syncingAttendees = false;
                }
            },
        });
    }
}

function mountDateControls(itemForm, eventData) {
    const eventDateRoot = itemForm
        .querySelector("[data-calendar-date-control=\"eventDate\"]");
    const startDateRoot = itemForm
        .querySelector("[data-calendar-date-control=\"startDate\"]");
    const endDateRoot = itemForm
        .querySelector("[data-calendar-date-control=\"endDate\"]");
    const onDateChange = () => validateEventDateRange(itemForm, { showError: true });
    let eventDateControl = null;
    let startDateControl = null;
    let endDateControl = null;

    if (eventDateRoot) {
        eventDateControl = createDateControl({
            root: eventDateRoot,
            name: "eventDate",
            nativeInputName: "eventDate",
            type: "date",
            label: "Date",
            value: eventData.eventDate,
            required: true,
            requiredMessage: "Date is required.",
        });
    }
    if (startDateRoot) {
        startDateControl = createDateControl({
            root: startDateRoot,
            name: "startDate",
            nativeInputName: "startDate",
            type: "date",
            label: "Start Date",
            value: eventData.startDate,
            required: true,
            onChange: (value) => {
                endDateControl?.setValue(value);
                onDateChange();
            },
        });
    }
    if (endDateRoot) {
        endDateControl = createDateControl({
            root: endDateRoot,
            name: "endDate",
            nativeInputName: "endDate",
            type: "date",
            label: "End Date",
            value: eventData.endDate,
            required: true,
            onChange: onDateChange,
        });
    }

    return {
        endDateControl,
        eventDateControl,
        startDateControl,
    };
}

function setSubmitting(itemForm, isSubmitting) {
    const submitButton = itemForm.querySelector("button[type=\"submit\"]");
    if (!submitButton) {return;}
    submitButton.disabled = isSubmitting;
    submitButton.classList.toggle("opacity-60", isSubmitting);
    submitButton.textContent = isSubmitting ? "Saving..." : "Save";
}

/**
 * Opens the Calendar event form modal.
 * @param {object|null} [event=null] Existing event data for edit mode.
 * @returns {void}
 */
export async function openEventFormModal(event = null) {
    const sourceEvent = await resolveEventFormSource(event);
    const eventData = getInitialEventData(sourceEvent);

    openStandardForm("calendar_event", eventData.id ? eventData : null, {
        getFieldsHtml: () => getEventFieldsHtml(sourceEvent),
        afterRender({ itemForm }) {
            const formTitle = document.querySelector("#form-title");
            const formHeaderActions = document.querySelector("#form-header-actions");
            if (formTitle && !eventData.id) {
                formTitle.textContent = "Add Event";
            }
            if (formHeaderActions) {
                formHeaderActions.innerHTML = getAllDayToggleHtml(eventData.isFullDay);
            }
            const dateControls = mountDateControls(itemForm, eventData);
            bindNativeFieldBehavior(itemForm);
            bindNativeFieldBehavior(formHeaderActions);
            bindTimeFieldBehavior(itemForm);
            bindAllDayFields(itemForm, dateControls);
            bindCalendarItemThresholdControl(itemForm, {
                itemType: CALENDAR_THRESHOLD_ITEM_TYPES.EVENT,
                getIsAllDay: () => document
                    .getElementById("calendar-event-all-day-toggle")
                    ?.checked === true,
            });
            mountUserControls(itemForm, eventData);
            itemForm.onsubmit = async (submitEvent) => {
                submitEvent.preventDefault();
                const controlValues = getEventControlValues(itemForm, {
                    validate: true,
                });
                if (
                    !controlValues.valid ||
                    !validateEventDateRange(itemForm, { showError: true }) ||
                    !validateEventTimeRange(itemForm) ||
                    !itemForm.reportValidity()
                ) {return;}

                setSubmitting(itemForm, true);
                const payload = collectEventData(itemForm, controlValues.values);
                const notifyUsers = itemForm
                    .querySelector("input[name=\"notifyUsers\"]")?.checked === true;
                const optimisticId = addOptimisticEventEntry(
                    payload,
                    eventData.id || null,
                );
                closeForm();

                try {
                    const response = eventData.id ?
                        await updateCalendarEvent(
                            eventData.id,
                            payload,
                            eventData.updatedAt,
                            notifyUsers,
                        ) :
                        await createCalendarEvent(payload, notifyUsers);
                    reconcileOptimisticEventEntry(optimisticId, response);
                } catch (error) {
                    console.error("Error saving Calendar event:", error);
                    discardOptimisticEntry(optimisticId);
                    showCalendarErrorModal(
                        "Event Save Failed",
                        error,
                        "Calendar event could not be saved.",
                    );
                } finally {
                    setSubmitting(itemForm, false);
                }
            };
        },
    });
}
